import { useEffect, useState } from 'react';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Button } from '@paulownia/shared/components/ui/button';
import { Repeat, Pause, Play, Trash2, Phone, Clock, StickyNote, Edit } from 'lucide-react';
import { type RecurringOrder, DAY_OF_WEEK_LABELS, type Market } from '@paulownia/shared/types';
import { onRecurringOrdersSnapshot, setRecurringOrderStatus, deleteRecurringOrder, updateRecurringOrder } from '@paulownia/shared/services/recurring-orders';
import { getMarketById } from '@paulownia/shared/services/markets';
import { listProducts } from '@paulownia/shared/services/products';
import { useTenant } from '@paulownia/shared/lib/tenant-context';
import { toast } from 'sonner';
import { type Product } from '@paulownia/shared/types';
import { RecurringOrderEditDialog } from './RecurringOrderEditDialog';

export function RecurringOrdersPage() {
    const { currentTenantId } = useTenant();
    const [recurringOrders, setRecurringOrders] = useState<RecurringOrder[]>([]);
    const [markets, setMarkets] = useState<Map<string, Market>>(new Map());
    const [products, setProducts] = useState<Product[]>([]);
    const [editingOrder, setEditingOrder] = useState<RecurringOrder | null>(null);

    useEffect(() => {
        if (!currentTenantId) return;

        const unsub = onRecurringOrdersSnapshot(currentTenantId, async (orders) => {
            setRecurringOrders(orders);

            // Load markets for all orders
            const marketIds = [...new Set(orders.map(o => o.marketId))];
            const marketEntries = await Promise.all(
                marketIds.map(async (id) => {
                    const m = await getMarketById(id);
                    return m ? [id, m] as [string, Market] : null;
                })
            );
            const newMarkets = new Map<string, Market>();
            for (const entry of marketEntries) {
                if (entry) newMarkets.set(entry[0], entry[1]);
            }
            setMarkets(newMarkets);
        });

        listProducts(currentTenantId).then(setProducts).catch(() => setProducts([]));

        return unsub;
    }, [currentTenantId]);

    const handleToggleStatus = async (order: RecurringOrder) => {
        const newStatus = order.status === 'active' ? 'paused' : 'active';
        try {
            await setRecurringOrderStatus(order.id, newStatus);
            toast.success(newStatus === 'active' ? 'Commande récurrente reprise.' : 'Commande récurrente mise en pause.');
        } catch {
            toast.error('Erreur lors du changement de statut.');
        }
    };

    const handleDelete = async (orderId: string) => {
        if (!window.confirm('Supprimer cette commande récurrente ? Les commandes déjà créées ne seront pas supprimées.')) return;
        try {
            await deleteRecurringOrder(orderId);
            toast.success('Commande récurrente supprimée.');
        } catch {
            toast.error('Erreur lors de la suppression.');
        }
    };

    const handleSaveEdit = async (orderId: string, updates: Parameters<typeof updateRecurringOrder>[1]) => {
        try {
            await updateRecurringOrder(orderId, updates);
            toast.success('Commande récurrente mise à jour.');
            setEditingOrder(null);
        } catch {
            toast.error('Erreur lors de la mise à jour.');
        }
    };

    const getProductName = (productId: string) => {
        return products.find(p => p.id === productId)?.name ?? productId;
    };

    if (!currentTenantId) return null;

    return (
        <div className="pb-20 p-4">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Repeat className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold">Commandes récurrentes</h1>
                    <p className="text-sm text-gray-500">Commandes automatiquement créées chaque semaine</p>
                </div>
            </div>

            {recurringOrders.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-gray-200 p-10 text-center">
                    <Repeat className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Aucune commande récurrente</p>
                    <p className="text-sm text-gray-400 mt-1">
                        Ouvrez une commande et cliquez sur "Rendre récurrent" pour en créer une.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {recurringOrders.map((order) => {
                        const market = markets.get(order.marketId);
                        return (
                            <div
                                key={order.id}
                                className={`rounded-xl border bg-white p-4 shadow-sm ${order.status === 'paused' ? 'opacity-60' : ''}`}
                            >
                                {/* Header row */}
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold text-gray-900">{order.customerName}</span>
                                            {order.status === 'active' ? (
                                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                                    <Repeat className="h-3 w-3 mr-1" />
                                                    Actif
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary">
                                                    <Pause className="h-3 w-3 mr-1" />
                                                    En pause
                                                </Badge>
                                            )}
                                        </div>
                                        {market && (
                                            <p className="text-sm text-gray-500 mt-0.5">
                                                Marché de {market.city} — {DAY_OF_WEEK_LABELS[market.dayOfWeek]}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Details */}
                                <div className="space-y-1.5 text-sm text-gray-600 mb-3">
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                                        <span>Retrait à {order.preferredPickupTime.replace(':', 'h')}</span>
                                    </div>
                                    {order.phoneNumber && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                            <span>{order.phoneNumber}</span>
                                        </div>
                                    )}
                                    {order.note && (
                                        <div className="flex items-start gap-2">
                                            <StickyNote className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                            <span className="italic">{order.note}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Products */}
                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                    <div className="space-y-1">
                                        {order.lines.map((line, idx) => (
                                            <div key={idx} className="flex items-center justify-between text-sm">
                                                <span className="text-gray-700">{getProductName(line.productId)}</span>
                                                <span className="font-medium text-gray-900">{line.quantity}×</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {order.lastGeneratedDate && (
                                    <p className="text-xs text-gray-400 mb-3">
                                        Dernière génération : {order.lastGeneratedDate}
                                    </p>
                                )}

                                {/* Actions */}
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setEditingOrder(order)}
                                        className="flex-1"
                                    >
                                        <Edit className="h-3.5 w-3.5 mr-1" />
                                        Modifier
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleToggleStatus(order)}
                                        className={`flex-1 ${order.status === 'active' ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                                    >
                                        {order.status === 'active' ? (
                                            <><Pause className="h-3.5 w-3.5 mr-1" />Pause</>
                                        ) : (
                                            <><Play className="h-3.5 w-3.5 mr-1" />Reprendre</>
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(order.id)}
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {editingOrder && (
                <RecurringOrderEditDialog
                    order={editingOrder}
                    products={products}
                    onSave={(updates) => handleSaveEdit(editingOrder.id, updates)}
                    onClose={() => setEditingOrder(null)}
                />
            )}
        </div>
    );
}
